import { useEffect, useMemo, useState } from 'react';
import { TopBar } from '../layout/TopBar.jsx';
import { TabBar } from '../layout/TabBar.jsx';
import { AnalysisProvider } from '../state/AnalysisContext.jsx';
import { useTabRouter } from '../state/useTabRouter.js';
import { HealthOverview } from '../panels/HealthOverview.jsx';
import { DcmaPanel } from '../panels/DcmaPanel.jsx';
import { DetailedFindings } from '../panels/DetailedFindings.jsx';

async function fetchAnalysis(reportId, signal) {
  // Stage 5.2 (B-GigaPayload100K): fetch the bounded dashboard.json projection,
  // not the full analysis.json (~50MB at 100K). Same render shape — every field
  // the panels read is present; per-check finding arrays are the §6-sorted
  // first-50 with a `<key>_total` for the true count.
  const res = await fetch(`/api/report/${encodeURIComponent(reportId)}/dashboard`, { signal });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Failed to load report ${reportId}: ${res.status} ${msg}`);
  }
  return res.json();
}

/**
 * ReportView — full analysis view for a single report. Extracted from App.jsx
 * so the app shell can route between this, the dashboard placeholder, and
 * public auth pages. `user` and `organization` are optional so legacy
 * unauthenticated `/?report=…` access keeps working.
 */
export function ReportView({ reportId, user = null, organization = null, onLogout = null }) {
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(reportId ? 'loading' : 'idle');
  const [activeTab, setActiveTab] = useTabRouter();

  useEffect(() => {
    if (!reportId) return undefined;
    let cancelled = false;
    const ctrl = new AbortController();
    setStatus('loading');
    fetchAnalysis(reportId, ctrl.signal)
      .then((data) => {
        if (cancelled) return;
        setAnalysis(data);
        setError(null);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.name === 'AbortError') return;
        setError(err.message);
        setStatus('error');
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [reportId]);

  const projectName = analysis?.projectInfo?.name || null;

  const contextValue = useMemo(
    () => ({ analysis, reportId, status, error }),
    [analysis, reportId, status, error],
  );

  return (
    <AnalysisProvider value={contextValue}>
      <div className="app-frame">
        <TopBar
          projectName={projectName}
          reportId={reportId}
          user={user}
          organization={organization}
          onLogout={onLogout}
        />
        <TabBar activeTab={activeTab} onSelect={setActiveTab} />
        <main className="main-content" id={`panel-${activeTab}`} role="tabpanel">
          <TabPanel
            status={status}
            error={error}
            activeTab={activeTab}
            reportId={reportId}
            analysis={analysis}
          />
        </main>
      </div>
    </AnalysisProvider>
  );
}

function TabPanel({ status, error, activeTab, reportId, analysis }) {
  if (!reportId) {
    return (
      <div className="shell-state">
        <h1 className="shell-state-title">No report selected</h1>
        <p>Append <code>?report=CW-YYYY-NNNN</code> to the URL to load a schedule analysis.</p>
      </div>
    );
  }
  if (status === 'loading') {
    return (
      <div className="shell-state">
        <p>Loading analysis for {reportId}…</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="shell-state shell-state-error" role="alert">
        <h1 className="shell-state-title">Couldn&rsquo;t load the report</h1>
        <p>{error}</p>
      </div>
    );
  }
  if (activeTab === 'health') {
    return <HealthOverview analysis={analysis} reportId={reportId} />;
  }
  if (activeTab === 'dcma') {
    return <DcmaPanel analysis={analysis} />;
  }
  if (activeTab === 'findings') {
    return <DetailedFindings analysis={analysis} reportId={reportId} />;
  }
  return (
    <div className="shell-state">
      <h1 className="shell-state-title">EVM Dashboards</h1>
      <p>EVM integrations ship in Phase 2.</p>
    </div>
  );
}
